// Section 24.12: building envelopes, encoding (lz-string over 500 kB), decoding (migrate, then
// validate with the schema before anything touches the store) and the storage wrapper.
import LZString from 'lz-string';
import {
  configSnapshot,
  contentVersion,
  ledgerOf,
  SimError,
  type Config,
  type GameState,
} from '../sim/api';
import { FORMAT_VERSION, type SaveEnvelope, type SaveSummary } from './envelope';
import { migrate } from './migrations';
import { rawEnvelopeSchema, saveEnvelopeSchema } from './schema';

const COMPRESS_OVER_BYTES = 500_000;
const COMPRESSED_PREFIX = 'lz:';

/** Minimal storage interface so persistence is testable without a browser. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Present on real Storage; lets the ring find its keys. Optional for test doubles. */
  readonly length?: number;
  key?(index: number): string | null;
}

export interface EnvelopeOptions {
  /** Include the state snapshot (default true). A shareable run omits it (Section 21). */
  withSnapshot?: boolean;
  /** Include the Ledger (exports). */
  withLedger?: boolean;
  /** The config the run is played on; defaults to the current config. */
  cfg?: Config;
}

export function summaryOf(state: GameState): SaveSummary {
  return {
    turn: state.turn,
    year: state.year,
    season: state.season,
    population: Object.keys(state.people).length,
    outcome: state.outcome?.kind ?? null,
    ironman: state.options.ironman,
  };
}

export function makeEnvelope(
  state: GameState,
  savedAt: string,
  o: EnvelopeOptions = {},
): SaveEnvelope {
  const withSnapshot = o.withSnapshot ?? true;
  const summary = summaryOf(state);
  return {
    formatVersion: FORMAT_VERSION,
    contentVersion: contentVersion(),
    configSnapshot: o.cfg === undefined ? configSnapshot() : structuredClone(o.cfg),
    seed: state.seed,
    options: state.options,
    decisionLog: state.decisionLog,
    snapshot: withSnapshot ? state : null,
    ledger: o.withLedger === true ? ledgerOf(state, o.cfg) : null,
    summary: withSnapshot ? summary : { ...summary, population: null },
    savedAt,
  };
}

export function encodeEnvelope(envelope: SaveEnvelope): string {
  const json = JSON.stringify(envelope);
  return json.length > COMPRESS_OVER_BYTES
    ? COMPRESSED_PREFIX + LZString.compressToUTF16(json)
    : json;
}

function parseText(text: string): unknown {
  const json = text.startsWith(COMPRESSED_PREFIX)
    ? LZString.decompressFromUTF16(text.slice(COMPRESSED_PREFIX.length))
    : text;
  try {
    return JSON.parse(json) as unknown;
  } catch {
    throw new SimError('schema', 'the file is not JSON');
  }
}

/** Parse, migrate to the current format and validate; throws SimError('schema'|'migration'). */
export function decodeEnvelope(text: string): SaveEnvelope {
  const raw = rawEnvelopeSchema.safeParse(parseText(text));
  if (!raw.success) throw new SimError('schema', 'not a Utopia save (no format version)');
  const migrated = migrate(raw.data);
  const parsed = saveEnvelopeSchema.safeParse(migrated);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const where = issue === undefined ? '' : ` at ${issue.path.join('.') || 'root'}`;
    throw new SimError('schema', `the save does not match the format${where}`);
  }
  return parsed.data as unknown as SaveEnvelope;
}

/** A write that may fail on quota (Section 24.12). */
export function isQuotaError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException)
    return error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED';
  return error instanceof Error && error.name === 'QuotaExceededError';
}

/** The browser's localStorage if usable, else null (private mode, tests, Node). */
export function browserStorage(): StorageLike | null {
  try {
    const storage = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (storage === undefined) return null;
    const probe = '__utopia_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}
